import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Download, FileText, Folder, FolderPlus, Presentation, Trash2, Upload } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import {
  UPDATE_ACCEPT,
  createFolder,
  deleteUpdateFile,
  deleteUpdateFolder,
  uploadUpdate,
  watchFiles,
  watchFolders,
  type UpdateFile,
  type UpdateFolder,
} from '../services/progressUpdates';
import { Button, EmptyState, Input, Modal, Spinner } from './ui';

type Props = { code: string; orgRole: 'owner' | 'admin' | 'member' | null };

const when = (f: { createdAt: { toDate(): Date } | null }) =>
  f.createdAt?.toDate().toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) ?? '';

const sizeLabel = (bytes: number) =>
  bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;

function message(e: unknown, fallback: string) {
  const code = (e as { code?: string })?.code ?? '';
  if (code === 'ppt-limit') return (e as Error).message;
  if (code === 'permission-denied' || code === 'storage/unauthorized') {
    return 'Not allowed. If your organisation’s trial or subscription has ended, it is read-only.';
  }
  return (e as Error)?.message || fallback;
}

/** Progress update folders, shared with everyone in the organisation. */
export default function ProgressUpdates(props: Props) {
  const { folderId } = useParams();
  return folderId ? <FolderView {...props} folderId={folderId} /> : <FolderList {...props} />;
}

function FolderList({ code, orgRole }: Props) {
  const { user } = useAuth();
  const [folders, setFolders] = useState<UpdateFolder[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  useEffect(
    () => watchFolders(code, setFolders, (e) => setError(message(e, 'Could not load the folders.'))),
    [code],
  );

  async function remove(f: UpdateFolder) {
    if (!window.confirm(`Delete the folder “${f.name}”${orgRole === 'owner' ? ' and every file in it' : ''}?`)) return;
    setError(null);
    try {
      await deleteUpdateFolder(f.id);
    } catch (e) {
      setError(message(e, 'Could not delete that folder.'));
    }
  }

  return (
    <div className="mx-auto max-w-3xl">
      <Link
        to="/"
        className="mb-1 inline-flex items-center gap-1 text-sm text-ink-400 hover:text-ink-100 pointer-coarse:min-h-11"
      >
        <ArrowLeft size={14} />
        Projects
      </Link>
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Progress updates</h1>
          <p className="text-sm text-ink-400">
            Shared with everyone in your organisation. PowerPoint files only.
          </p>
        </div>
        <Button variant="primary" icon={<FolderPlus size={16} />} onClick={() => setCreating(true)}>
          New folder
        </Button>
      </div>

      {error && <p className="mb-3 rounded-lg border border-red-800 bg-red-950 p-3 text-sm text-red-200">{error}</p>}

      {folders === null ? (
        <div className="flex justify-center py-16">
          <Spinner className="size-6" />
        </div>
      ) : folders.length === 0 ? (
        <EmptyState
          icon={<Folder size={28} />}
          title="No folders yet"
          body="Make a folder, then upload progress updates into it for the whole organisation to see."
        />
      ) : (
        <ul className="space-y-2">
          {folders.map((f) => (
            <li
              key={f.id}
              className="flex items-center gap-3 rounded-xl border border-ink-700 bg-ink-900 px-4 py-3 hover:border-ink-600"
            >
              <Folder size={18} className="shrink-0 text-accent-400" />
              <Link to={`/updates/${f.id}`} className="min-w-0 flex-1 pointer-coarse:py-2">
                <p className="truncate text-sm font-medium text-ink-100">{f.name}</p>
                <p className="truncate text-xs text-ink-400">
                  {[f.createdByName, when(f)].filter(Boolean).join(' · ')}
                </p>
              </Link>
              {(orgRole === 'owner' || f.createdByUid === user?.uid) && (
                <button
                  className="icon-btn text-ink-400 hover:bg-ink-800 hover:text-red-300"
                  aria-label={`Delete ${f.name}`}
                  onClick={() => void remove(f)}
                >
                  <Trash2 size={15} />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      <NewFolderDialog code={code} open={creating} onClose={() => setCreating(false)} />
    </div>
  );
}

function NewFolderDialog({ code, open, onClose }: { code: string; open: boolean; onClose: () => void }) {
  const { user } = useAuth();
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setName('');
      setError(null);
    }
  }, [open]);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || !user || busy) return;
    setBusy(true);
    setError(null);
    try {
      await createFolder(code, name, { uid: user.uid, name: user.displayName ?? user.email });
      onClose();
    } catch (err) {
      setError(message(err, 'Could not create the folder.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="New folder">
      <form onSubmit={save} className="space-y-4">
        <Input
          autoFocus
          value={name}
          maxLength={80}
          onChange={(e) => setName(e.target.value)}
          placeholder="Folder name"
          aria-label="Folder name"
        />
        {error && <p className="rounded-lg border border-red-800 bg-red-950 p-3 text-sm text-red-200">{error}</p>}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" loading={busy} disabled={!name.trim()}>
            Create
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function FolderView({ code, orgRole, folderId }: Props & { folderId: string }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [folder, setFolder] = useState<UpdateFolder | null | undefined>(undefined);
  const [files, setFiles] = useState<UpdateFile[] | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(
    () =>
      watchFolders(code, (all) => setFolder(all.find((f) => f.id === folderId) ?? null), () => setFolder(null)),
    [code, folderId],
  );
  useEffect(
    () => watchFiles(code, folderId, setFiles, (e) => setError(message(e, 'Could not load the files.'))),
    [code, folderId],
  );

  async function upload(list: FileList) {
    if (!user) return;
    setError(null);
    for (const file of Array.from(list)) {
      try {
        setProgress(0);
        await uploadUpdate(code, folderId, file, { uid: user.uid, name: user.displayName ?? user.email }, setProgress);
      } catch (e) {
        setError(`${file.name}: ${message(e, 'upload failed.')}`);
      }
    }
    setProgress(null);
  }

  async function remove(f: UpdateFile) {
    if (!window.confirm(`Delete ${f.name}?`)) return;
    setError(null);
    try {
      await deleteUpdateFile(folderId, f.id);
    } catch (e) {
      setError(message(e, 'Could not delete that file.'));
    }
  }

  if (folder === null) {
    return (
      <div className="mx-auto max-w-3xl">
        <EmptyState
          title="This folder no longer exists"
          action={<Button onClick={() => navigate('/updates')}>Back to progress updates</Button>}
        />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl">
      <Link
        to="/updates"
        className="mb-1 inline-flex items-center gap-1 text-sm text-ink-400 hover:text-ink-100 pointer-coarse:min-h-11"
      >
        <ArrowLeft size={14} />
        Progress updates
      </Link>
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <h1 className="min-w-0 truncate text-xl font-semibold">{folder?.name ?? ''}</h1>
        <Button
          variant="primary"
          icon={<Upload size={16} />}
          loading={progress !== null}
          onClick={() => inputRef.current?.click()}
        >
          {progress !== null ? `${Math.round(progress * 100)}%` : 'Upload PowerPoint'}
        </Button>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={UPDATE_ACCEPT}
          className="hidden"
          onChange={(e) => {
            const list = e.target.files;
            if (list?.length) void upload(list);
            e.target.value = '';
          }}
        />
      </div>

      {error && <p className="mb-3 rounded-lg border border-red-800 bg-red-950 p-3 text-sm text-red-200">{error}</p>}

      {files === null ? (
        <div className="flex justify-center py-16">
          <Spinner className="size-6" />
        </div>
      ) : files.length === 0 ? (
        <EmptyState
          icon={<FileText size={28} />}
          title="Nothing uploaded yet"
          body="Upload a PowerPoint file. Everyone in your organisation can open and download it."
        />
      ) : (
        <ul className="space-y-2">
          {files.map((f) => (
            <li key={f.id} className="flex items-center gap-3 rounded-xl border border-ink-700 bg-ink-900 px-4 py-3">
              {f.kind === 'pdf' ? (
                <FileText size={18} className="shrink-0 text-red-400" />
              ) : (
                <Presentation size={18} className="shrink-0 text-amber-400" />
              )}
              <a
                href={f.downloadUrl}
                target="_blank"
                rel="noreferrer"
                className="min-w-0 flex-1 pointer-coarse:py-2"
              >
                <p className="truncate text-sm font-medium text-ink-100 hover:underline">{f.name}</p>
                <p className="truncate text-xs text-ink-400">
                  {[f.uploadedByName, when(f), sizeLabel(f.size)].filter(Boolean).join(' · ')}
                </p>
              </a>
              <a
                href={f.downloadUrl}
                target="_blank"
                rel="noreferrer"
                className="icon-btn text-ink-400 hover:bg-ink-800 hover:text-ink-100"
                aria-label={`Download ${f.name}`}
              >
                <Download size={15} />
              </a>
              {(orgRole === 'owner' || f.uploadedByUid === user?.uid) && (
                <button
                  className="icon-btn text-ink-400 hover:bg-ink-800 hover:text-red-300"
                  aria-label={`Delete ${f.name}`}
                  onClick={() => void remove(f)}
                >
                  <Trash2 size={15} />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
